import { ArrowLeft } from 'lucide-react';

import React from 'react';

import { LanguageSwitch } from '@/components/common/LanguageSwitch';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { Wordmark } from '@/components/common/Wordmark';
import { Footer } from '@/components/layout/Footer';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

import { isEmbeddedInMobileApp } from '@/lib/embeddedMode';

interface StaticPageLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

export function StaticPageLayout({
  children,
  title,
  subtitle,
}: StaticPageLayoutProps) {
  const isEmbedded = isEmbeddedInMobileApp();

  return (
    <div className="min-h-screen bg-canvas dark:bg-canvas-dark flex flex-col">
      {/* Header — hidden in embedded mode; the in-app browser provides its own chrome (Done / back / forward). */}
      {!isEmbedded && (
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
            <RouteChangeLink
              href="/"
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <Wordmark height={18} />
            </RouteChangeLink>
            <div className="flex items-center gap-2">
              <LanguageSwitch variant="clean" />
              <ThemeToggle />
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {title}
            </h1>
            {subtitle && (
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {subtitle}
              </p>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
            {children}
          </div>
        </div>
      </main>

      {/* Footer — hidden in embedded mode; its quick-links duplicate mobile Settings rows and the social links open another external browser. */}
      {!isEmbedded && <Footer />}
    </div>
  );
}
